import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { Textarea } from './ui/textarea';
import { MapPin, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { staffPosition, USUAL_FIELD_OPS_ROLES } from '../utils/staffRoles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FieldOpsAssignmentModal = ({ open, onClose, operationId, currentFieldOpsId, onSuccess }) => {
  const [fieldOpsList, setFieldOpsList] = useState([]);
  const [selectedFieldOpsId, setSelectedFieldOpsId] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingFieldOps, setFetchingFieldOps] = useState(false);

  const usualFieldOps = fieldOpsList.filter(fo => USUAL_FIELD_OPS_ROLES.includes((fo.role || '').toLowerCase()));
  const otherStaff = fieldOpsList.filter(fo => !USUAL_FIELD_OPS_ROLES.includes((fo.role || '').toLowerCase()));
  const selectedStaff = fieldOpsList.find(fo => fo.id === selectedFieldOpsId);
  const isCrossRole = !!selectedStaff && !USUAL_FIELD_OPS_ROLES.includes((selectedStaff.role || '').toLowerCase());

  useEffect(() => {
    if (open) {
      fetchFieldOps();
      setSelectedFieldOpsId(currentFieldOpsId || '');
      setReason('');
    }
  }, [open, currentFieldOpsId]);

  const fetchFieldOps = async () => {
    try {
      setFetchingFieldOps(true);
      // Tout le staff interne est assignable (PM 03/11/2026)
      const response = await axios.get(`${API}/internal-users/field-ops/list?is_active=true&include_all_staff=true`, {
        headers: {
          'Authorization': 'Bearer cookie-auth'
        }
      });
      setFieldOpsList(response.data || []);
    } catch (error) {
      console.error('Error fetching field ops:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger la liste des Field Ops",
        variant: "destructive"
      });
    } finally {
      setFetchingFieldOps(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedFieldOpsId) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner un Field Ops",
        variant: "destructive"
      });
      return;
    }

    if (isCrossRole && !reason.trim()) {
      toast({
        title: "Motif requis",
        description: "Indiquez pourquoi ce profil est assigné au terrain",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API}/operations/${operationId}/field-ops`, {
        fieldOpsId: selectedFieldOpsId,
        reason: reason || undefined
      }, {
        headers: {
          'Authorization': 'Bearer cookie-auth'
        }
      });

      toast({
        title: "✅ Field Ops assigné",
        description: "Le Field Ops a été assigné avec succès"
      });

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Error assigning Field Ops:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible d'assigner le Field Ops",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-blue-600">
            <MapPin className="w-5 h-5" />
            Assigner le suivi terrain
          </DialogTitle>
          <DialogDescription>
            Sélectionnez la personne chargée de la coordination terrain — tous les profils internes sont disponibles
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Field Ops Selection */}
          <div className="space-y-2">
            <Label htmlFor="fieldOps">Suivi terrain *</Label>
            {fetchingFieldOps ? (
              <div className="flex items-center justify-center p-4 border rounded-md">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                <span className="text-sm text-gray-500">Chargement...</span>
              </div>
            ) : (
              <Select value={selectedFieldOpsId} onValueChange={setSelectedFieldOpsId}>
                <SelectTrigger data-testid="fieldops-select-trigger">
                  <SelectValue placeholder="Sélectionner un membre du staff" />
                </SelectTrigger>
                <SelectContent>
                  {fieldOpsList.length === 0 && (
                    <SelectItem value="none" disabled>
                      Aucun membre du staff disponible
                    </SelectItem>
                  )}
                  {usualFieldOps.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>Équipe terrain</SelectLabel>
                      {usualFieldOps.map((fo) => (
                        <SelectItem key={fo.id} value={fo.id} data-testid={`fieldops-option-${fo.id}`}>
                          {fo.firstName} {fo.lastName} — {staffPosition(fo)}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                  {otherStaff.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>Autres équipes</SelectLabel>
                      {otherStaff.map((fo) => (
                        <SelectItem key={fo.id} value={fo.id} data-testid={`fieldops-option-${fo.id}`}>
                          {fo.firstName} {fo.lastName} — {staffPosition(fo)}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
            )}
            <p className="text-xs text-gray-500">
              {fieldOpsList.length} membre(s) du staff disponible(s)
            </p>
          </div>

          {isCrossRole && (
            <Alert className="bg-amber-50 border-amber-200" data-testid="fieldops-cross-role-warning">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 text-xs">
                <strong>{selectedStaff?.firstName} {selectedStaff?.lastName}</strong> n&apos;est pas
                rattaché à l&apos;équipe terrain. L&apos;assignation reste possible : elle sera tracée dans
                l&apos;historique et la personne recevra une notification. Indiquez le motif.
              </AlertDescription>
            </Alert>
          )}

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason">Motif {isCrossRole ? '*' : '(optionnel)'}</Label>
            <Textarea
              id="reason"
              placeholder="Ex: expertise maritime, disponibilité sur le port, remplacement congés…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              data-testid="fieldops-reason-input"
            />
          </div>

          {/* Info Box */}
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-xs text-blue-700">
              <strong>ℹ️ Rôle du Field Ops :</strong> Le Field Ops coordonne les opérations 
              sur le terrain (port, entrepôt, site de chargement). Il travaille en collaboration 
              avec l'opérateur en charge.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          <Button
            onClick={handleAssign}
            disabled={loading || !selectedFieldOpsId}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="fieldops-assign-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Assignation...
              </>
            ) : (
              <>
                <MapPin className="w-4 h-4 mr-2" />
                Assigner
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FieldOpsAssignmentModal;
